import { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useActor } from '@/auth/pin/phoneSession'
import { useFormat, useI18n } from '@/i18n'
import { newId } from '@/lib/ids'
import { enqueueCommand } from '@/offline'
import {
  Banner,
  BilingualLabel,
  Button,
  Card,
  CardBody,
  CardHeader,
  EmptyState,
  Field,
  IntegerInput,
  PhotoCapture,
  PickerSheet,
  ScanField,
  useToast,
  type ScanMeta,
} from '@/ui'
import type { SealSamplePayload } from '../commands'
import '../commands'
import { runParentIds, type ProcessState } from '../projection'
import { checkFreshCode, sampleCandidates, type SampleCandidate } from '../model/rules'
import { useRefusal } from '../components/useText'
import { outputName } from '../components/labels'
import { ProcessScreen } from '../components/ProcessScreen'

/**
 * F6.7 — QC sample from the brand batch that goes to packing; and (F7-B1.b) a confirming sample
 * of a held batch when the quality person asks for one. Sealed with the site's own seal range.
 */
export function QcSamplePage() {
  return <ProcessScreen title="process.qc.title">{(state) => <QcSample state={state} />}</ProcessScreen>
}

function QcSample({ state }: { state: ProcessState }) {
  const { t, locale } = useI18n()
  const fmt = useFormat()
  const toast = useToast()
  const actor = useActor()
  const navigate = useNavigate()
  const refusal = useRefusal()
  const [params] = useSearchParams()
  const packing = sampleCandidates(state)
  const confirming = sampleCandidates(state, { confirming: true })
  const all: Array<SampleCandidate & { purpose: SealSamplePayload['purpose'] }> = [
    ...packing.map((c) => ({ ...c, purpose: 'PACKING' as const })),
    ...confirming.map((c) => ({ ...c, purpose: 'CONFIRMING' as const })),
  ]
  const [unitId, setUnitId] = useState<string | null>(() => {
    const u = params.get('unit')
    return u && all.some((c) => c.unit_id === u) ? u : all.length === 1 ? all[0]!.unit_id : null
  })
  const [seal, setSeal] = useState<{ code: string; entry: ScanMeta['entry'] } | null>(null)
  const [grams, setGrams] = useState<number | null>(null)
  const [photo, setPhoto] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const pick = all.find((c) => c.unit_id === unitId) ?? null
  const unit = pick ? state.units.get(pick.unit_id) : undefined

  if (!all.length)
    return (
      <EmptyState
        title={<BilingualLabel k="process.qc.none" layout="stacked" />}
        description={t('process.qc.noneBody')}
      />
    )

  const sealIt = async () => {
    if (!pick || !seal || !grams || !photo) return
    setSaving(true)
    try {
      const sample_id = newId()
      const payload: SealSamplePayload = {
        sample_id,
        kind: 'BATCH_QC',
        subject_type: 'UNIT',
        subject_id: pick.unit_id,
        seal_qr_payload: seal.code,
        entry: seal.entry,
        weight_g: grams,
        photo_id: photo,
        purpose: pick.purpose,
      }
      await enqueueCommand({
        id: sample_id,
        type: 'seal_sample',
        payload,
        actorId: actor.user_id,
        mediaIds: [photo],
        parentIds: pick.run ? runParentIds(pick.run) : [],
      })
      toast.success(t('process.qc.done', { seal: seal.code }))
      navigate(-1)
    } catch (e) {
      toast.error(t('process.saveFailed'), e instanceof Error ? e.message : undefined)
    } finally {
      setSaving(false)
    }
  }

  const label = (c: (typeof all)[number]) => {
    const u = state.units.get(c.unit_id)
    const name = c.run ? outputName(state, c.run.step_code, c.output_key, locale, t) : (u?.grade ?? '')
    return `${u?.code ?? c.unit_id} · ${name}`
  }

  return (
    <div className="flex flex-col gap-4">
      <Field label={<BilingualLabel k="process.qc.batch" />} required>
        <PickerSheet
          title={<BilingualLabel k="process.qc.batch" />}
          options={all.map((c) => ({
            value: c.unit_id,
            label: label(c),
            description:
              c.purpose === 'CONFIRMING'
                ? t('process.qc.confirmingHint')
                : `${state.units.get(c.unit_id)?.farmer_code ?? ''} · ${fmt.weight(c.net_g)}`,
          }))}
          value={unitId}
          onChange={setUnitId}
        />
      </Field>
      {pick?.purpose === 'CONFIRMING' && <Banner tone="warn">{t('process.qc.confirmingBanner')}</Banner>}
      {pick && (
        <Card>
          <CardHeader
            eyebrow={<BilingualLabel k="process.qc.sacks" />}
            title={t('process.sacksN', { n: pick.sacks.length })}
            description={unit ? `${unit.farmer_code ?? ''} · ${fmt.weight(pick.net_g)}` : undefined}
          />
          <CardBody>
            <ul className="flex flex-wrap gap-2">
              {pick.sacks.map((s) => (
                <li key={s} className="rounded-chip border border-rule px-2 py-1 font-mono text-[13px]">
                  {s}
                </li>
              ))}
            </ul>
            <p className="mt-3 text-[13px] text-muted">{t('process.qc.howTo')}</p>
          </CardBody>
        </Card>
      )}
      <Field label={<BilingualLabel k="process.qc.seal" />} hint={t('process.qc.sealHint')} required>
        <ScanField
          value={seal?.code ?? null}
          sheetTitle={<BilingualLabel k="process.qc.seal" />}
          validate={(c) => {
            const v = checkFreshCode(state, c, 'QC_SEAL', actor.user_id)
            return v.ok ? null : refusal(v.reason)
          }}
          onChange={(c, meta) => setSeal(c ? { code: c.trim().toUpperCase(), entry: meta?.entry ?? 'SCAN' } : null)}
        />
      </Field>
      <Field label={<BilingualLabel k="process.qc.weight" />} required>
        <IntegerInput value={grams} onChange={setGrams} maxDigits={5} />
      </Field>
      <Field label={<BilingualLabel k="process.qc.photo" />} required>
        <PhotoCapture value={photo} onChange={setPhoto} />
      </Field>
      <Button size="xl" fullWidth disabled={!pick || !seal || !grams || !photo} loading={saving} onClick={() => void sealIt()}>
        <BilingualLabel k="process.qc.sealIt" />
      </Button>
      <p className="text-[13px] text-muted">{t('process.qc.dispatchNote')}</p>
    </div>
  )
}
